import React, { useEffect, useState } from 'react';
import DailyScheduleTimeline from './DailyScheduleTimeline';
import { DateTime } from 'luxon';
import {
    useHttpGetModes,
    useHttpPutDailyBlueprint,
    useGetAllDeviceDailyConfigs,
    useHttpPostDeviceDailyModeConfig,
    useHttpDeleteDeviceDailyModeConfig,
    useHttpPutDeviceDailyModeConfig,
    useHttpGetDevice,
    useHttpGetDeviceSchedule,
} from '../../hooks';
import Loader from '../../components/Loader';
import ModeSchedulerModal from '../../components/Modal/ModeSchedulerModal';
import withUrlParam from '../../hocs/withUrlParam';
import { HistoricalData } from './HistoricalData';
import DailyScheduleSummaryTable from './DailyScheduleSummaryTable';
import InfoTooltip from '../../components/InfoTooltip/InfoTooltip';
import { getUserTimezoneOffsetInHours } from '../../utils/timeUtils';
import useHttpGetPredefinedSchedules from '../../hooks/modeScheduling/useHttpGetPredefinedSchedules';
import ToggleSwitch from '../../components/Common/Switch';
import DayScheduleTimelineDisplay from './DailyScheduleTimelineDisplay';
import api from '../../api';
import schedulesApi from '../../schedulerApi';
import { ModesLegend, BatteryStatus, ActiveMode } from './ModeSchedulerSummary';
import { Button, Select } from '../../components/Common';
import { mapWeeklyBlueprint } from '../../utils/modeScheduleUtils';
import { ToastContainer, toast } from 'react-toastify';

function formatDateTimeString(time) {
    if (!time) {
        return '';
    }
    const dateTime = DateTime.fromISO(time);
    return dateTime.isValid ? dateTime.toFormat('yyyy-MM-dd HH:mm:ss') : '';
}

const scheduleModes = [
    {
        value: 'predefined',
        label: 'Predefined Schedule',
    },
    {
        value: 'device-specific',
        label: 'Device-Specific Schedule',
    },
];

function Scheduler({ paramValue }) {
    const deviceId = paramValue;

    const [followSchedule, setFollowSchedule] = useState(false);
    const [scheduleMode, setScheduleMode] = useState('predefined'); // either 'predefined' or 'device-specific'
    const { data: device, isReady: deviceIsReady } = useHttpGetDevice(deviceId);
    const { data: modes } = useHttpGetModes();
    const { data: deviceSchedule } = useHttpGetDeviceSchedule(deviceId);
    const { data: predefinedSchedules } = useHttpGetPredefinedSchedules();

    const [loading, setLoading] = useState(true);

    async function fetchDeviceFollowsSchedule() {
        try {
            const followsSchedule = await api.get_device_follows_schedule(deviceId);
            setFollowSchedule(followsSchedule);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }
    async function setDeviceFollowsSchedule(follow) {
        try {
            await api.set_device_follows_schedule(deviceId, follow);
            fetchDeviceFollowsSchedule();
        } catch (error) {
            console.error(error);
        }
    }

    useEffect(() => {
        fetchDeviceFollowsSchedule();
    }, [deviceId]);

    useEffect(() => {
        if (!deviceSchedule) {
            setScheduleMode('predefined');
        } else if (deviceSchedule && deviceSchedule.id && predefinedSchedules) {
            const predefinedSchedule = predefinedSchedules.find(s => s.id === deviceSchedule.id);
            if (predefinedSchedule) {
                setScheduleMode('predefined');
            } else {
                setScheduleMode('device-specific');
            }
        } else if (!predefinedSchedules || predefinedSchedules.length === 0) {
            setScheduleMode('device-specific');
        }
    }, [deviceSchedule, predefinedSchedules]);

    if (loading || !deviceIsReady) {
        return <Loader />;
    }

    return (
        <>
            <ToastContainer />
            <div className="card-wrapper mode-scheduler">
                <div className="mode-scheduler__content">
                    <div className="mode-scheduler__weekly-canvas">
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                alignItems: 'left',
                                paddingBottom: '10px',
                            }}
                        >
                            <h4>Follow Schedule</h4>
                            <ToggleSwitch
                                isActive={followSchedule}
                                onToggle={() => {
                                    setDeviceFollowsSchedule(!followSchedule);
                                    setFollowSchedule(!followSchedule);
                                }}
                                onClick={() => setFollowSchedule(!followSchedule)}
                            />
                        </div>
                        <div style={{ opacity: followSchedule ? 1 : 0.5, pointerEvents: followSchedule ? 'auto' : 'none' }}>
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    borderBottom: '1px solid #ccc',
                                    paddingBottom: '10px',
                                }}
                            >
                                <div
                                    style={{
                                        padding: '2px',
                                        margin: '10px',
                                        display: 'flex',
                                        flexDirection: 'row',
                                        gap: '10px',
                                        justifyItems: 'center',
                                        alignContent: 'center',
                                    }}
                                >
                                    <h4 style={{ fontSize: '15px', paddingTop: '10px' }}>Mode</h4>
                                    <Select
                                        value={scheduleMode}
                                        options={scheduleModes}
                                        onChange={e => setScheduleMode(e.target.value)}
                                        name={'Schedule Mode'}
                                    />
                                </div>
                                <div className="summary">
                                    <ModesLegend modes={modes} />
                                </div>
                            </div>
                            <div style={{ margin: '10px' }}>
                                {scheduleMode === 'predefined' && (
                                    <PredefinedScheduleSelection
                                        predefinedSchedules={predefinedSchedules}
                                        deviceId={deviceId}
                                        deviceSchedule={deviceSchedule}
                                        modes={modes}
                                    />
                                )}
                                {scheduleMode === 'device-specific' && <DeviceSpecificScheduleBuilder deviceId={deviceId} modes={modes} />}
                            </div>
                        </div>
                    </div>
                    <div className="mode-scheduler__summary-section">
                        <BatteryStatus device={device} />
                        {followSchedule && <ActiveMode modes={modes} deviceSchedule={deviceSchedule} />}
                        <GatewayDeviceMetadata device={device} ready={deviceIsReady} />
                    </div>
                </div>
            </div>
            <div className="card-wrapper mode-scheduler" style={{ marginTop: '20px' }}>
                <HistoricalData deviceId={deviceId} />
            </div>
        </>
    );
}

function PredefinedScheduleSelection({ deviceId, deviceSchedule, modes, predefinedSchedules }) {
    const [assignedPredefinedSchedule, setAssignedPredefinedSchedule] = useState(null);
    const [selectedSchedule, setSelectedSchedule] = useState(null);

    async function assignSchedule(schedule) {
        if (schedule) {
            try {
                await api.assign_schedule_to_device(schedule.id, deviceId);
                setAssignedPredefinedSchedule(schedule);
            } catch (error) {
                toast.error('Failed to assign schedule. Error: ' + error.message);
                console.error('Error assigning schedule:', error);
            }
        }
    }

    async function unassignSchedule(schedule) {
        try {
            await api.unassign_device_from_schedule(schedule.id, deviceId);
            setAssignedPredefinedSchedule(null);
            setSelectedSchedule(null);
        } catch (error) {
            toast.error('Failed to unassign schedule. Error: ' + error.message);
            console.error('Error unassigning previous schedule:', error);
        }
    }

    useEffect(() => {
        if (deviceSchedule && predefinedSchedules) {
            const predefinedSchedule = predefinedSchedules.find(s => s.id === deviceSchedule.id);
            if (predefinedSchedule) {
                setAssignedPredefinedSchedule(predefinedSchedule);
            } else {
                setAssignedPredefinedSchedule(null);
            }
            setSelectedSchedule(predefinedSchedule || predefinedSchedules[0] || null);
        } else if (predefinedSchedules && predefinedSchedules.length > 0) {
            setSelectedSchedule(predefinedSchedules[0]);
        }
    }, [deviceSchedule, predefinedSchedules]);

    return (
        <div className="mode-scheduler">
            <h3>Predefined Schedules</h3>
            <div style={{ marginBottom: '10px', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                <p>Assigned Schedule: {assignedPredefinedSchedule ? assignedPredefinedSchedule.name : 'None'}</p>
                <Button
                    onClick={() => {
                        if (assignedPredefinedSchedule) {
                            unassignSchedule(assignedPredefinedSchedule);
                        }
                    }}
                    text={assignedPredefinedSchedule ? 'Unassign Schedule' : 'No Schedule Assigned'}
                    disabled={!assignedPredefinedSchedule}
                />
            </div>
            <div style={{ padding: '3px', display: 'flex', flexDirection: 'row', gap: '10px' }}>
                {predefinedSchedules.length > 0 ? (
                    <>
                        <Select
                            value={selectedSchedule?.id || ''}
                            options={predefinedSchedules.map(schedule => ({ value: schedule.id, label: schedule.name }))}
                            onChange={e => {
                                const schedule = predefinedSchedules.find(s => s.id === e.target.value);
                                setSelectedSchedule(schedule);
                            }}
                            placeholder="Select a schedule"
                        />
                        <Button
                            onClick={() => {
                                if (selectedSchedule) {
                                    assignSchedule(selectedSchedule);
                                }
                            }}
                            text="Assign"
                        />
                    </>
                ) : (
                    <span style={{ color: 'gray' }}>No predefined schedules available</span>
                )}
            </div>
            {selectedSchedule && <SelectedPredefinedSchedule predefinedSchedule={selectedSchedule} modes={modes} />}
        </div>
    );
}

function SelectedPredefinedSchedule({ predefinedSchedule, modes }) {
    return (
        <div style={{ padding: '20px', border: '1px solid #eee', marginTop: '10px', borderRadius: '5px' }}>
            <h4>{predefinedSchedule.name ?? 'Unnamed Schedule'}</h4>
            <DayScheduleTimelineDisplay modes={modes} schedule={predefinedSchedule} />
            <PredefinedScheduleSummaryTable predefinedSchedule={predefinedSchedule} modes={modes} />
        </div>
    );
}

const slotToTime = slot => {
    if (slot === 96) {
        return '24:00';
    }
    const hours = Math.floor(slot / 4);
    const minutes = (slot % 4) * 15;
    return DateTime.fromObject({ hour: hours, minute: minutes }).toFormat('HH:mm');
};

function PredefinedScheduleSummaryTable({ predefinedSchedule, modes }) {
    const [collapsed, setCollapsed] = useState(true);

    const getModeName = id => {
        if (id === 0) {
            return 'idle';
        }
        const rawName = modes.find(m => m.id === id)?.name || 'Unknown';
        return formatKeyLabel(rawName);
    };

    const formatKeyLabel = key => key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());

    return (
        <div className="daily-schedule-summary-table">
            <div className="daily-schedule-summary-table__header" onClick={() => setCollapsed(!collapsed)}>
                <div className="daily-schedule-summary-table__title">Summary</div>
                <div className="daily-schedule-summary-table__collapse">
                    <i className={`fa ${collapsed ? 'fa-chevron-right' : 'fa-chevron-down'}`} />
                </div>
            </div>
            {!collapsed && (
                <table className="daily-schedule-summary-table__table">
                    <thead>
                        <tr>
                            <th>From</th>
                            <th>To</th>
                            <th>Mode</th>
                            <th>Config</th>
                        </tr>
                    </thead>
                    <tbody>
                        {predefinedSchedule.configs.map((block, idx) => (
                            <tr key={idx}>
                                <td>{slotToTime(block.from_slot)}</td>
                                <td>{slotToTime(block.to_slot)}</td>
                                <td>{getModeName(block.mode)}</td>
                                <td className="daily-schedule-summary-table--config">
                                    {block.config && Object.keys(block.config).length > 0 ? (
                                        <div className="daily-schedule-summary-table__table--config--details">
                                            {Object.entries(block.config)
                                                .filter(([key]) => key.toLowerCase() !== 'mode')
                                                .map(([key, value]) => (
                                                    <div key={key} style={{ breakInside: 'avoid' }}>
                                                        <strong>{formatKeyLabel(key)}</strong>:{' '}
                                                        {value === null || value === undefined ? (
                                                            <span className="daily-schedule-summary-table--config--no-data">Not set</span>
                                                        ) : (
                                                            formatValue(value)
                                                        )}
                                                    </div>
                                                ))}
                                        </div>
                                    ) : (
                                        <span className="daily-schedule-summary-table--config--no-data">–</span>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
}

function formatValue(value) {
    if (typeof value === 'number') {
        return value.toFixed(2);
    }
    if (typeof value === 'string' && !isNaN(Date.parse(value))) {
        return DateTime.fromISO(value).toFormat('yyyy-MM-dd HH:mm:ss');
    }
    if (typeof value === 'string') {
        return value;
    }
    if (typeof value === 'object' && value !== null) {
        if ('relative' in value && 'value' in value) {
            if (value.relative) {
                return `${value.value * 100}%`;
            } else {
                return `${value.value} kW`;
            }
        }
        return JSON.stringify(value, null, 2);
    }
    return 'Unknown';
}

function GatewayDeviceMetadata({ device, ready }) {
    const deviceInfo = device?.info ?? {};

    if (!ready || !device) {
        return (
            <div className="summary__card summary__card--active">
                <h3 className="summary__title">Metadata</h3>
                <Loader />;
            </div>
        );
    }

    return (
        <div className="summary__card summary__card--active">
            <h3 className="summary__title">Metadata</h3>

            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }} className="device-metadata-table">
                <tbody>
                    <tr>
                        <td>Inverter Manufacturer</td>
                        <td>{deviceInfo?.inverter_manufacturer ?? 'Unknown'}</td>
                    </tr>
                    <tr>
                        <td>Inv. Model</td>
                        <td>{deviceInfo?.inverter_model ?? 'Unknown'}</td>
                    </tr>
                    <tr>
                        <td>Inv. Serial Number</td>
                        <td>{deviceInfo?.inverter_serial_number ?? 'Unknown'}</td>
                    </tr>
                    <tr style={{ borderBottom: '1px solid #ccc' }}>
                        <td>Inv. Firmware Version</td>
                        <td>{deviceInfo?.inverter_firmware_version ?? 'Unknown'}</td>
                    </tr>
                    <tr>
                        <td>Gateway Hardware</td>
                        <td>{deviceInfo?.gateway_hardware_version ?? 'Unknown'}</td>
                    </tr>
                    <tr>
                        <td>Gateway Firmware</td>
                        <td>{deviceInfo?.gateway_firmware_version ?? 'Unknown'}</td>
                    </tr>
                </tbody>
            </table>

            <h3 className="summary__title" style={{ marginTop: '20px' }}>
                Parameters
            </h3>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '10px' }}>
                <tbody>
                    <tr>
                        <td>Energy Capacity</td>
                        <td>{deviceInfo?.parameters?.energy_capacity ?? 0} kWh</td>
                    </tr>
                    <tr>
                        <td>Battery Max Power</td>
                        <td>{deviceInfo?.parameters?.battery_max_power ?? 0} kW</td>
                    </tr>
                    <tr>
                        <td>Inverter Rated Power</td>
                        <td>{deviceInfo?.parameters?.inverter_rated_power ?? 0} kW</td>
                    </tr>
                    <tr>
                        <td>Charge Power Measured</td>
                        <td>{deviceInfo?.parameters?.max_charging_power_measured ?? 0} kW</td>
                    </tr>
                    <tr>
                        <td>Discharge Power Measured</td>
                        <td>{deviceInfo?.parameters?.max_discharging_power_measured ?? 0} kW</td>
                    </tr>
                    <tr>
                        <td>Energy Capacity Measured</td>
                        <td>{deviceInfo?.parameters?.energy_capacity_measured ?? 0} kWh</td>
                    </tr>
                    <tr>
                        <td>Last Measured</td>
                        <td>{formatDateTimeString(deviceInfo?.parameters?.last_measured)}</td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
}

function DeviceSpecificScheduleBuilder({ deviceId, modes }) {
    const { data: deviceSchedule, isReady: scheduleIsReady, refetch: refetchSchedule } = useHttpGetDeviceSchedule(deviceId);

    const {
        configsByDayId,
        isReady: configsReady,
        refetch: refetchConfigs,
    } = useGetAllDeviceDailyConfigs(deviceId, deviceSchedule?.weekly_bp, deviceSchedule?.daily_bp);

    const isDailyBp = !!deviceSchedule?.daily_bp;

    const today = DateTime.now();
    const startOfWeek = today.startOf('week');
    const weekDays = isDailyBp ? [today.startOf('day')] : Array.from({ length: 7 }, (_, i) => startOfWeek.plus({ days: i }));

    const [openModal, setOpenModal] = useState(false);
    const [modalData, setModalData] = useState(null);

    const updateDailyBlueprint = useHttpPutDailyBlueprint();
    const postDeviceDailyConfig = useHttpPostDeviceDailyModeConfig();
    const updateDeviceDailyConfig = useHttpPutDeviceDailyModeConfig();
    const deleteDeviceDailyConfig = useHttpDeleteDeviceDailyModeConfig();

    async function setupDeviceSchedule() {
        try {
            await schedulesApi.setup_device_weekly_schedule(deviceId);
            await refetchSchedule();
        } catch (error) {
            console.error('Error setting up device schedule:', error);
            toast.error('Failed to set up device schedule. Please try again later.');
        }
    }
    /*
    dayBlocks = {
      "monday": {
        "0": { mode: 0, config: {} },
        "1": { mode: 0, config: {} },
        "2": { mode: 0, config: {} },
        ...
      }
    }
  */
    let dayBlocks = {};
    if (deviceSchedule) {
        dayBlocks = mapWeeklyBlueprint(deviceSchedule, configsByDayId);
    }

    const convertTimeToSlots = (startTime, duration) => {
        const start = DateTime.fromISO(startTime);
        const end = start.plus({ hours: duration.hours, minutes: duration.minutes });
        const startSlot = Math.floor(start.hour * 4 + start.minute / 15);
        let endSlot = Math.floor(end.hour * 4 + end.minute / 15) - 1;

        if (endSlot <= 0) {
            endSlot = 96;
        }

        return { startSlot, endSlot };
    };

    const getModeId = modeNameOrId => {
        if (typeof modeNameOrId === 'number') {
            return modeNameOrId;
        }

        const mode = modes.find(m => m.name === modeNameOrId);
        if (!mode) {
            console.warn(`Mode not found: ${modeNameOrId}`);
            return 0;
        }
        return mode.id;
    };

    const checkOverlaps = (modeSchedule, dayBlocks) => {
        const IDLE_MODE_ID = 0;
        if (!dayBlocks) {
            return false;
        }

        const currentSchedules = dayBlocks[modeSchedule.startDate.toLowerCase()];

        if (!currentSchedules) {
            return true;
        }
        const { startSlot, endSlot } = convertTimeToSlots(modeSchedule.startTime, modeSchedule.duration);
        const { slots } = currentSchedules;

        for (let i = startSlot; i <= endSlot; i++) {
            const currentMode = slots[i].mode;
            if (currentMode !== IDLE_MODE_ID) {
                return true;
            }
        }

        return false;
    };

    const composeNewDailySchedule = (modeSchedule, currentDayBlocks) => {
        const { startTime, duration, mode, config } = modeSchedule;
        const { startSlot, endSlot } = convertTimeToSlots(startTime, duration);
        const { slots } = currentDayBlocks;

        const newSlots = Object.fromEntries(Object.entries(slots).map(([k, v]) => [k, { ...v }]));

        for (let i = startSlot; i <= endSlot; i++) {
            newSlots[i] = {
                mode,
                config,
            };
        }

        return newSlots;
    };

    const prepareNewDailyBlueprint = newDailySchedule => {
        const blueprint = [];

        for (let i = 0; i < 96; i++) {
            const slot = newDailySchedule[i];
            const modeId = getModeId(slot?.mode);
            blueprint.push(modeId);
        }

        return blueprint;
    };

    const createDeviceScheduleConfig = modeSchedule => {
        const { startTime, duration, mode, config } = modeSchedule;
        const { startSlot, endSlot } = convertTimeToSlots(startTime, duration);
        const modeId = getModeId(mode);

        return {
            mode: Number(modeId),
            from_slot: startSlot,
            to_slot: endSlot,
            config: config || {},
        };
    };

    const saveMode = async (modeSchedule, blueprintSource) => {
        const currentBlocks = blueprintSource || dayBlocks;
        if (!currentBlocks) {
            console.warn('No current blocks found for saving mode');
            return;
        }

        const overlaps = checkOverlaps(modeSchedule, currentBlocks);
        if (overlaps) {
            console.warn('Mode overlaps with existing schedule');
            return;
        }

        const currentDayToChange = currentBlocks[modeSchedule.startDate.toLowerCase()];
        const newDailySchedule = composeNewDailySchedule(modeSchedule, currentDayToChange);
        const newDailyBlueprint = prepareNewDailyBlueprint(newDailySchedule);

        const dailyBlueprintId = currentDayToChange.id;

        await Promise.all([
            updateDailyBlueprint(dailyBlueprintId, newDailyBlueprint, 'updated'),
            postDeviceDailyConfig(deviceId, dailyBlueprintId, [createDeviceScheduleConfig(modeSchedule)]),
        ]);
    };

    const handleSave = async data => {
        if (!dayBlocks) {
            console.warn('No day blocks available for saving mode');
            return;
        }
        const { recurrence, ...rest } = data;
        if (recurrence === 'every_day' && !isDailyBp) {
            await Promise.all(
                weekDays.map(date => {
                    const dayName = date.toFormat('cccc').toLowerCase();
                    const modeSchedule = { ...rest, startDate: dayName };
                    const blocksForThatDay = { [dayName]: dayBlocks[dayName] };
                    return saveMode(modeSchedule, blocksForThatDay);
                })
            );
        } else {
            await saveMode(rest, dayBlocks);
        }
        // eslint-disable-next-line no-unused-vars
        const newConfigs = await refetchConfigs();
        // eslint-disable-next-line no-unused-vars
        const newSchedule = await refetchSchedule();
    };

    const switchWithNewDailySchedule = (modeSchedule, initialData, currentDayBlocks) => {
        const IDLE_MODE_ID = 0;
        const { slots } = currentDayBlocks;
        const newSlots = { ...slots };

        const { startSlot: newStartSlot, endSlot: newEndSlot } = convertTimeToSlots(modeSchedule.startTime, modeSchedule.duration);
        const { startSlot: initialStartSlot, endSlot: initialEndSlot } = convertTimeToSlots(initialData.startTime, initialData.duration);

        for (let i = initialStartSlot; i <= initialEndSlot; i++) {
            newSlots[i] = {
                mode: IDLE_MODE_ID,
                config: {},
            };
        }

        for (let i = newStartSlot; i <= newEndSlot; i++) {
            if (newSlots[i]?.mode !== IDLE_MODE_ID) {
                console.warn('New range overlaps with existing schedule.');
                return null;
            }
        }

        const modeId = getModeId(modeSchedule.mode);
        for (let i = newStartSlot; i <= newEndSlot; i++) {
            newSlots[i] = {
                mode: modeId,
                config: modeSchedule.config || {},
            };
        }

        return newSlots;
    };

    function toSlot(decimalHours) {
        const snapped = Math.round(decimalHours * 4);
        return snapped;
    }

    function slotToHHMM(slotIndex) {
        const hours = Math.floor(slotIndex / 4);
        const minutes = (slotIndex % 4) * 15;
        return { hours, minutes };
    }

    const handleEdit = async (data, initialData) => {
        const { startSlot, endSlot } = convertTimeToSlots(data.startTime, data.duration);

        const currentBlocks = dayBlocks;
        const currentDayToChange = currentBlocks[data.startDate.toLowerCase()];

        const newDailySchedule = switchWithNewDailySchedule(data, initialData, currentDayToChange);

        if (!newDailySchedule) {
            console.warn('Edit aborted due to overlap');
            return;
        }

        const blueprintSlots = prepareNewDailyBlueprint(newDailySchedule);

        const dailyBlueprintId = currentDayToChange.id;
        const newConfig = {
            mode: getModeId(data.mode),
            from_slot: startSlot,
            to_slot: endSlot,
            config: data.config,
        };

        await updateDailyBlueprint(dailyBlueprintId, blueprintSlots, 'updated');

        if (data.configId) {
            await updateDeviceDailyConfig(deviceId, dailyBlueprintId, data.configId, newConfig);
        } else {
            await postDeviceDailyConfig(deviceId, dailyBlueprintId, [createDeviceScheduleConfig(data)]);
        }

        // eslint-disable-next-line no-unused-vars
        const refreshSchedule = await refetchSchedule();
        // eslint-disable-next-line no-unused-vars
        const refreshConfig = await refetchConfigs();
    };

    const deleteMode = (startSlot, endSlot) => {
        const currentDayToChange = dayBlocks[modalData.startDate.toLowerCase()];
        const { slots } = currentDayToChange;

        const newSlots = { ...slots };

        for (let i = startSlot; i <= endSlot; i++) {
            newSlots[i] = {
                mode: 0,
                config: {},
                configId: null,
            };
        }

        return newSlots;
    };

    const handleDeleteBlock = async data => {
        const { startTime, duration } = data;
        const { startSlot, endSlot } = convertTimeToSlots(startTime, duration);
        const newDailyBlueprint = deleteMode(startSlot, endSlot);

        const dailyBlueprintId = dayBlocks[data.startDate.toLowerCase()].id;
        const newBlueprint = prepareNewDailyBlueprint(newDailyBlueprint);
        const configId = data.configId;

        await updateDailyBlueprint(dailyBlueprintId, newBlueprint, 'updated');
        if (configId) {
            await deleteDeviceDailyConfig(deviceId, dailyBlueprintId, configId);
        }

        // eslint-disable-next-line no-unused-vars
        const refreshSchedule = await refetchSchedule();
        // eslint-disable-next-line no-unused-vars
        const refreshConfig = await refetchConfigs();
    };

    const handleBlockCreate = block => {
        const startDate = DateTime.fromISO(block.date);
        const durationHours = block.end - block.start;

        const newData = {
            startDate: startDate.toFormat('cccc'),
            startTime: startDate
                .set({
                    hour: Math.floor(block.start),
                    minute: Math.round((block.start % 1) * 60),
                })
                .toFormat('HH:mm'),
            duration: {
                hours: Math.floor(durationHours),
                minutes: Math.round((durationHours % 1) * 60),
            },
            mode: 'idle',
            recurrence: 'once',
            config: {},
            date: block.date,
            configId: null,
        };

        setModalData(newData);
        setOpenModal(true);
    };

    const handleEditBlock = (block, date) => {
        const startSlot = toSlot(block.start);
        const endSlot = toSlot(block.end);

        const startDate = DateTime.fromISO(date).set(slotToHHMM(startSlot));
        const slotCount = endSlot - startSlot;

        const durHours = Math.floor(slotCount / 4);
        const durMinutes = (slotCount % 4) * 15;

        setModalData({
            startDate: startDate.toFormat('cccc'),
            startTime: startDate.toFormat('HH:mm'),
            duration: {
                hours: durHours,
                minutes: durMinutes,
            },
            mode: block.label,
            config: block.config || {},
            configId: block.configId || null,
            date: block.date,
            recurrence: 'once',
        });

        setOpenModal(true);
    };

    const printTimezoneTooltip = () => {
        const userTimezoneOffset = getUserTimezoneOffsetInHours();
        const sign = userTimezoneOffset >= 0 ? '+' : '-';
        const absOffset = Math.abs(userTimezoneOffset);

        return `Note: All scheduled modes and the timeline view below are aligned with UTC time. Your current local time is UTC${sign}${absOffset}. Please take this into account when configuring schedules.`;
    };

    if (scheduleIsReady && !deviceSchedule) {
        return (
            <div className="mode-scheduler__no-schedule">
                <h3>No Device Schedule Found</h3>
                <Button
                    text="Create Weekly Schedule"
                    onClick={() => {
                        setupDeviceSchedule();
                    }}
                />
            </div>
        );
    }

    if (!scheduleIsReady || !configsReady) {
        return <Loader />;
    }

    return (
        <div>
            <div className="mode-scheduler__header">
                <div className="mode-scheduler__header--info">
                    <h3>Device Specific Schedule</h3>
                    <div className="mode-scheduler__header--timezone">
                        <h5>( UTC timezone )</h5>
                        <InfoTooltip tooltip={printTimezoneTooltip()} />
                    </div>
                </div>
                <Button
                    className="device-alerts__back-button"
                    onClick={() => {
                        const defaultDate = DateTime.now().toISODate();
                        setModalData({
                            mode: 'idle',
                            startDate: 'Monday',
                            startTime: null,
                            duration: { hours: 0, minutes: 0 },
                            date: defaultDate,
                        });
                        setOpenModal(true);
                    }}
                    text="Schedule"
                />
                <ModeSchedulerModal
                    open={openModal}
                    setOpen={setOpenModal}
                    initialData={modalData}
                    onDelete={handleDeleteBlock}
                    onSave={handleSave}
                    onEdit={handleEdit}
                    weekDays={weekDays}
                    modes={modes}
                    isDailyBp={isDailyBp}
                />
            </div>
            <div className="mode-scheduler__content">
                <div className="mode-scheduler__weekly-canvas">
                    {weekDays.map(date => (
                        <>
                            <DailyScheduleTimeline
                                key={date.toISODate()}
                                modes={modes}
                                schedules={dayBlocks[DateTime.fromISO(date).toFormat('cccc').toLowerCase()] || []}
                                day={isDailyBp ? 'Every Day' : date.toFormat('ccc')}
                                isToday={date.toISODate() === today.toISODate()}
                                date={date.toISODate()}
                                onCreateBlock={handleBlockCreate}
                                onEditBlock={handleEditBlock}
                            />
                            <DailyScheduleSummaryTable
                                title={'Summary'}
                                dayData={dayBlocks[DateTime.fromISO(date).toFormat('cccc').toLowerCase()] || []}
                                modes={modes}
                            />
                        </>
                    ))}
                </div>
            </div>
        </div>
    );
}

export { Scheduler as ModeScheduler };
export default withUrlParam('id')(Scheduler);
